import { Injectable, inject } from '@angular/core';
import { Firestore, collectionData, collection, query, orderBy, limit, addDoc, serverTimestamp, where } from '@angular/fire/firestore';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PhotoPrice } from './photo-price';
import { Size } from '../orders/order.model';
import { CurrentUserService } from '../../shared/services/current-user.service';

@Injectable({
  providedIn: 'root'
})
export class PhotoPricesService {
  readonly #firestore = inject(Firestore);
  readonly #currentUser = inject(CurrentUserService);

  /** Latest prices of the current user's company. */
  getLatestPhotoPrice(): Observable<PhotoPrice | undefined> {
    return this.#currentUser.company$.pipe(
      switchMap(company => {
        if (!company) return of(undefined);

        // Prices are versioned: the newest document of the company is the current one
        const photoPricesQuery = query(
          collection(this.#firestore, 'prices'),
          where('companyId', '==', company.companyId),
          orderBy('lastUpdated', 'desc'),
          limit(1)
        );

        return (collectionData(photoPricesQuery, { idField: 'id' }) as Observable<PhotoPrice[]>).pipe(
          map(([latest]) => latest)
        );
      })
    );
  }

  getSizes(): Observable<Size[]> {
    return this.getLatestPhotoPrice().pipe(
      map(photoPrice => photoPrice?.sizes ?? [])
    );
  }

  /** Saves a new prices version for the current user's company. */
  async createPhotoPrice(photoPrice: Pick<PhotoPrice, 'sizes'>): Promise<string> {
    const company = await this.#currentUser.requireCompany();

    const docRef = await addDoc(collection(this.#firestore, 'prices'), {
      ...photoPrice,
      ...company,
      lastUpdated: serverTimestamp()
    });
    return docRef.id;
  }
}
